"use client";

import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { type ReactNode, useEffect, useRef } from "react";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { useConsentStore } from "@/lib/analytics/consent";
import { identifyUser, resetUser, trackPageView } from "@/lib/services/posthog";

function PostHogProviderInner({ children }: { children: ReactNode }) {
	const { user } = useSupabase();
	const canTrackPostHog = useConsentStore((state) => state.canTrackPostHog);
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const hasTrackedInitialPage = useRef(false);
	const lastTrackedPage = useRef<string>("");

	// Initialize PostHog when lazy-loaded script is ready
	useEffect(() => {
		const initializePostHog = () => {
			if (
				typeof window !== "undefined" &&
				window.posthog &&
				process.env.NODE_ENV === "production" &&
				process.env.NEXT_PUBLIC_POSTHOG_KEY
			) {
				posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
					// Use proxy to avoid ad blockers
					api_host: "/ingest",
					ui_host: "https://us.posthog.com",

					// Vanilla fix for 431: Use localStorage+cookie (PostHog default and recommended)
					// This prevents cookie size issues by storing minimal data in cookies
					persistence: "localStorage+cookie",

					// Performance optimizations
					capture_pageview: false, // We handle pageviews manually for privacy control
					capture_pageleave: false, // Disabled - uses deprecated 'unload' event

					// 2025 best practice - only create profiles for identified users
					person_profiles: "identified_only",

					// Disable unused features completely (prevents unnecessary requests)
					disable_session_recording: true,
					disable_surveys: true,
					autocapture: false,

					// Completely disable /decide endpoint calls (feature flags, toolbar, etc.)
					// This prevents 401 errors on /ingest/flags/ endpoint
					advanced_disable_decide: true,

					// Success callback
					loaded: () => {
						// PostHog loaded successfully
					},
				});
			}
		};

		// Listen for the lazy-loaded PostHog script
		window.addEventListener('posthog-loaded', initializePostHog);

		// Also check if PostHog is already available (in case script loaded before this component mounted)
		if (window.posthog) {
			initializePostHog();
		}

		return () => {
			window.removeEventListener('posthog-loaded', initializePostHog);
		};
	}, []);

	// Track page views on route changes
	useEffect(() => {
		if (!canTrackPostHog()) return;

		const url = `${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ""}`;

		// Skip if this is the same page (avoids duplicate tracking on search param changes)
		if (lastTrackedPage.current === url) return;

		// Track page view
		trackPageView(pathname || "/", {
			url,
			search_params: searchParams?.toString() || "",
			user_id: user?.id || null,
			authenticated: !!user,
		});

		lastTrackedPage.current = url;
		hasTrackedInitialPage.current = true;
	}, [pathname, searchParams, user, canTrackPostHog]);

	// Handle user identification and reset
	useEffect(() => {
		if (!canTrackPostHog()) return;

		if (user) {
			// Simple identification with just email
			identifyUser(user.id, { email: user.email });
		} else if (hasTrackedInitialPage.current) {
			// Only reset user if we've already started tracking
			resetUser();
		}
	}, [user, canTrackPostHog]);

	// Only render PostHog provider if consent is given
	if (!canTrackPostHog()) {
		return <>{children}</>;
	}

	return <PHProvider client={posthog}>{children}</PHProvider>;
}

export function PostHogProvider({ children }: { children: ReactNode }) {
	return <PostHogProviderInner>{children}</PostHogProviderInner>;
}
